import type { MenuScreen as BaseMenuScreen, Scene } from '@bn-games/ui-shell';
import type { RoomClient, SessionEntry } from '@bn-games/room-client';
import type { Settings } from '../settings/types.js';
import type { ActiveSession, GameRecord } from '../history/types.js';
import type { HistoryStore } from '../history/HistoryStore.js';
import type { ReplayReturnTarget } from '../history/ReplayScene.js';
import type { ClientMessage, ServerMessage } from '../network/protocol.js';
import type { PlayerCount } from '../game/variants/types.js';
import { getRuleSet, type VariantId } from '../game/variants/index.js';

export type { Scene };

export type Navigate = (scene: Scene) => void;

/**
 * Who controls the human-facing seat(s). `ai` fills every non-local seat with
 * an AI; `local` is hot-seat — every seat passes the device. `online` routes
 * moves through the WS server (wired in M8).
 */
export type Opponent = 'ai' | 'local' | 'online';

/**
 * Table size + variant in a single enum. Drives both `playerCount` and
 * `variant` (`all-fives-2v2` requires 4 players + partnership). Surfaced as a
 * single badge so the user never picks an inconsistent pair.
 */
export type TableMode = '2p' | '3p' | '4p' | '4p-2v2';

export const TABLE_MODES: readonly TableMode[] = ['2p', '3p', '4p', '4p-2v2'];

export function tableModeFromSettings(s: Settings): TableMode {
  if (getRuleSet(s.variant).partnership) return '4p-2v2';
  if (s.playerCount === 3) return '3p';
  if (s.playerCount === 4) return '4p';
  return '2p';
}

/** A variant's free-for-all sibling within the same family (identity for FFA ids). */
export function soloSibling(variant: VariantId): VariantId {
  switch (variant) {
    case 'goat':
    case 'goat-2v2':
      return 'goat';
    case 'telefon':
    case 'telefon-2v2':
      return 'telefon';
    case 'all-fives':
    case 'all-fives-2v2':
      return 'all-fives';
  }
}

/** A variant's partnership sibling within the same family (identity for 2v2 ids). */
export function partnershipSibling(variant: VariantId): VariantId {
  switch (soloSibling(variant)) {
    case 'goat':
      return 'goat-2v2';
    case 'telefon':
      return 'telefon-2v2';
    default:
      return 'all-fives-2v2';
  }
}

/**
 * TableMode → (variant, playerCount). We preserve the user's variant
 * family (Козёл / Телефон / All Fives) by swapping only between the
 * partnership / non-partnership sibling within the same family.
 */
export function settingsForTableMode(
  mode: TableMode,
  currentVariant: Settings['variant'],
): Pick<Settings, 'variant' | 'playerCount'> {
  switch (mode) {
    case '2p':
      return { variant: soloSibling(currentVariant), playerCount: 2 };
    case '3p':
      return { variant: soloSibling(currentVariant), playerCount: 3 };
    case '4p':
      return { variant: soloSibling(currentVariant), playerCount: 4 };
    case '4p-2v2':
      return { variant: partnershipSibling(currentVariant), playerCount: 4 };
  }
}

export interface MenuContext {
  readonly navigate: Navigate;
  readonly getSettings: () => Settings;
  readonly updateSettings: (partial: Partial<Settings>) => void;
  readonly getOpponent: () => Opponent;
  readonly setOpponent: (opponent: Opponent) => void;

  /**
   * The seat the human chose for the next vs-AI game (engine seat index,
   * always 0..playerCount-1 against the active `seatsFor(playerCount)`).
   * Defaults to 0 ('south'). Cleared after the game starts.
   */
  readonly getPendingLocalSeat: () => number;
  readonly setPendingLocalSeat: (seat: number) => void;

  /**
   * The seat the host wants when creating an online room. Sent as
   * `preferredSeat` in CREATE_ROOM so the server can rotate the seats array
   * accordingly. Defaults to 0 ('south').
   */
  readonly getPendingOnlineSeat: () => number;
  readonly setPendingOnlineSeat: (seat: number) => void;

  /**
   * The seat-count the host wants when creating an online room. Sent as
   * `seatCount` in CREATE_ROOM (clamped server-side to the domino namespace's
   * range). Defaults to 2.
   */
  readonly getPendingOnlineSeatCount: () => PlayerCount;
  readonly setPendingOnlineSeatCount: (count: PlayerCount) => void;

  // Online flow — wired in main.ts via the room machinery. Online screens
  // call these instead of touching network/* directly.
  readonly roomClient: RoomClient<ClientMessage, ServerMessage>;
  readonly createRoom: () => void;
  readonly joinRoom: (code: string) => void;
  readonly cancelRoom: () => void;
  readonly resumeSession: () => void;
  readonly getSavedSession: () => SessionEntry | null;

  // Active-game resume — persists across navigation/refresh for AI + local
  // games via ActiveGameStore. Online games are not resumable from this path
  // (no session token in the seed) and the Play menu filters them out.
  readonly getActiveSession: () => ActiveSession | null;
  readonly resumeActiveGame: () => void;
  readonly discardActiveSession: (onConfirm?: () => void) => void;

  // History/replay. Screens consult the store directly; opening a record
  // routes through main.ts so the replay scene can swap into the canvas.
  readonly historyStore: HistoryStore;
  readonly openReplay: (record: GameRecord, returnTo: ReplayReturnTarget) => void;

  /**
   * Returns + clears any share-link prefill code (set when the page boots via
   * `#room=CODE` without a token). OnlineJoinScreen consumes this on mount.
   */
  readonly consumePendingJoinCode: () => string | null;
}

export type MenuScreen = BaseMenuScreen<MenuContext>;
